import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'fileDelivery.userDefaults';

export interface UserDefaults {
  defaultDataRegion?: string;
  defaultServiceAccount?: string;
}

interface UseUserDefaultsReturn {
  defaults: UserDefaults;
  setDefaultDataRegion: (value: string | undefined) => void;
  setDefaultServiceAccount: (value: string | undefined) => void;
  clearDefaults: () => void;
  isLoaded: boolean;
}

const loadDefaultsFromStorage = (): UserDefaults => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored) as UserDefaults;
    }
  } catch {
    console.warn('Failed to load user defaults from localStorage');
  }

  return {};
};

const saveDefaultsToStorage = (defaults: UserDefaults): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(defaults));
  } catch {
    console.warn('Failed to save user defaults to localStorage');
  }
};

export const useUserDefaults = (): UseUserDefaultsReturn => {
  const [defaults, setDefaults] = useState<UserDefaults>({});
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const loaded = loadDefaultsFromStorage();
    setDefaults(loaded);
    setIsLoaded(true);
  }, []);

  const setDefaultDataRegion = useCallback((value: string | undefined) => {
    setDefaults((prev) => {
      const updated = { ...prev, defaultDataRegion: value };
      saveDefaultsToStorage(updated);
      return updated;
    });
  }, []);

  const setDefaultServiceAccount = useCallback((value: string | undefined) => {
    setDefaults((prev) => {
      const updated = { ...prev, defaultServiceAccount: value };
      saveDefaultsToStorage(updated);
      return updated;
    });
  }, []);

  const clearDefaults = useCallback(() => {
    setDefaults({});
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      console.warn('Failed to clear user defaults from localStorage');
    }
  }, []);

  return {
    defaults,
    setDefaultDataRegion,
    setDefaultServiceAccount,
    clearDefaults,
    isLoaded,
  };
};
